import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Play } from "lucide-react";
import { useSite } from "../../context/SiteContext";

const ease = [0.22, 1, 0.36, 1];
const LINES = [
  { t: "Kesintisiz", c: "text-white" },
  { t: "Eğlence,", c: "outline-text" },
  { t: "Tek Tık Uzağında.", c: "text-[#8a8dff]" },
];

const Line = ({ children, i, className }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.5 + i * 0.14, ease }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  ["500.000+", "içerik"],
  ["4", "platform"],
  ["6 saat", "ücretsiz test"],
];

export default function Hero({ go }) {
  const { trial } = useSite();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 80, damping: 15 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 80, damping: 15 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const moonY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden bg-[#0d0b3d] pt-44 pb-16 md:pt-48">
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)", backgroundSize: "80px 80px" }} />
      <motion.div style={{ y: moonY }} className="absolute -right-28 top-20 opacity-60 md:opacity-100 md:right-[8%] md:top-28">
        <motion.div
          initial={{ y: 260, opacity: 0, scale: 0.7 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 2.2, delay: 0.1, ease }}
          className="moon h-64 w-64 md:h-[26rem] md:w-[26rem] rounded-full"
        />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-8">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-[#c9c6f0]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#8a8dff] animate-pulse" /> Dolunay TV · IPTV
          </motion.p>
          <h1 className="font-display font-extrabold uppercase leading-[0.95] tracking-tight text-[2.6rem] sm:text-6xl lg:text-[5.6rem]" data-testid="hero-title">
            {LINES.map((l, i) => <Line key={l.t} i={i} className={l.c}>{l.t}</Line>)}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9, ease }}
            className="mt-8 max-w-xl text-base md:text-lg text-[#c9c6f0] leading-relaxed">
            500 binden fazla film, dizi ve canlı yayın. Android, iOS, bilgisayar ve TV'den tek hesapla gir; ister Türkiye'de, ister dünyanın her yerinde izle.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.9, ease }}
            className="mt-10 flex flex-wrap gap-3">
            <a href="#paketler" onClick={(e) => { e.preventDefault(); go("#paketler"); }} data-testid="hero-packages-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-[#5b5fd6] px-7 py-4 font-semibold text-white hover:bg-[#6d71ec] transition-colors">
              Paketleri Gör <ArrowUpRight size={18} className="transition-transform group-hover:rotate-45" />
            </a>
            <a href={trial} target="_blank" rel="noreferrer" data-testid="hero-trial-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-4 font-semibold text-white hover:bg-white hover:text-[#0d0b3d] transition-colors">
              <Play size={16} /> 6 Saat Ücretsiz Dene
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-4 [perspective:1200px]">
          <motion.div style={{ rotateX: rx, rotateY: ry, y: imgY }}
            initial={{ opacity: 0, clipPath: "inset(100% 0 0 0 round 28px)" }}
            animate={{ opacity: 1, clipPath: "inset(0% 0 0 0 round 28px)" }}
            transition={{ duration: 1.4, delay: 0.8, ease }}
            className="relative aspect-[4/5] w-full max-w-sm mx-auto lg:mx-0 [transform-style:preserve-3d]">
            <img src="/img/stadium.jpg" alt="Canlı maç yayını" className="absolute inset-0 h-full w-full object-cover rounded-[28px]" />
            <div className="absolute inset-0 rounded-[28px] bg-gradient-to-t from-[#0d0b3d] via-[#0d0b3d]/20 to-transparent" />
            <div className="absolute left-5 top-5 flex items-center gap-2 rounded-full bg-black/40 backdrop-blur px-3 py-1 text-xs font-semibold" style={{ transform: "translateZ(40px)" }}>
              <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" /> CANLI
            </div>
            <div className="absolute bottom-6 left-6 right-6" style={{ transform: "translateZ(60px)" }}>
              <p className="font-display text-3xl font-extrabold">500.000+</p>
              <p className="text-sm text-[#c9c6f0]">film, dizi ve canlı yayın tek ekranda</p>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 1 }}
        className="relative z-10 mx-auto max-w-7xl px-5 md:px-10 mt-16 grid grid-cols-3 gap-4 border-t border-white/10 pt-6">
        {STATS.map(([v, l]) => (
          <div key={l} data-testid={`hero-stat-${l.replace(/\s/g, "-")}`}>
            <p className="font-display text-lg sm:text-3xl font-semibold">{v}</p>
            <p className="text-xs sm:text-sm text-[#c9c6f0] mt-1">{l}</p>
          </div>
        ))}
      </motion.div>
    </section>
  );
}
